import { useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Empty, ErrorBox, Ltr, Row, Segmented, Spinner, StatusChip } from '../components/ui.js';
import type { IssueSummary } from '../lib/types.js';

interface WorkResponse {
  counts: Record<string, number | null>;
  site: { open: number | null; unassigned: number | null; overdue: number | null };
  assigned: IssueSummary[];
  projects: Array<{ key: string; name: string }>;
  quickQueries: string[];
}

const MINE_TILES: Array<{ key: string; labelKey: string }> = [
  { key: 'assignedToMe', labelKey: 'home.assigned' },
  { key: 'myOverdue', labelKey: 'home.overdue' },
  { key: 'myDueToday', labelKey: 'home.due_soon' },
  { key: 'myActiveSprint', labelKey: 'home.sprint' },
];

export function HomePage(): JSX.Element {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [scope, setScope] = useState<'mine' | 'all'>('mine');

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () =>
      api<{ activeSite: { name: string } | null; user: { firstName: string | null } }>('/me'),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['work'],
    queryFn: () => api<WorkResponse>('/me/work'),
  });

  if (isLoading) return <Spinner />;
  if (error) {
    return (
      <div className="page">
        <ErrorBox error={error} />
      </div>
    );
  }

  const assignedCount = data?.counts.assignedToMe ?? 0;
  const hasNothingAssigned = (assignedCount ?? 0) === 0 && (data?.assigned ?? []).length === 0;

  const siteTiles: Array<{ jql: string; labelKey: string; value: number | null }> = [
    {
      jql: 'resolution = Unresolved ORDER BY updated DESC',
      labelKey: 'home.all_open',
      value: data?.site.open ?? null,
    },
    {
      jql: 'assignee IS EMPTY AND resolution = Unresolved ORDER BY created DESC',
      labelKey: 'home.unassigned',
      value: data?.site.unassigned ?? null,
    },
    {
      jql: 'duedate < now() AND resolution = Unresolved ORDER BY duedate ASC',
      labelKey: 'home.overdue',
      value: data?.site.overdue ?? null,
    },
  ];

  return (
    <div className="page">
      <div className="header">
        <h1>{t('home.title').replace(/<[^>]+>/g, '')}</h1>
        <span className="muted small">{me?.activeSite?.name ?? ''}</span>
      </div>

      <Segmented
        value={scope}
        onChange={setScope}
        options={[
          { value: 'mine', label: t('home.scope_mine') },
          { value: 'all', label: t('home.scope_all') },
        ]}
      />

      {scope === 'mine' ? (
        <>
          <div className="grid">
            {MINE_TILES.map((tile) => (
              <button
                key={tile.key}
                type="button"
                className="tile"
                onClick={() => navigate(`/issues?quick=${tile.key}`)}
              >
                <div className="tile-value">{data?.counts[tile.key] ?? '–'}</div>
                <div className="tile-label">{t(tile.labelKey)}</div>
              </button>
            ))}
          </div>

          {hasNothingAssigned ? (
            <Card>
              <Empty text={t('home.nothing_assigned')} />
              <p className="small muted" style={{ margin: '0 0 12px' }}>
                {t('home.nothing_assigned_hint')}
              </p>
              <button
                type="button"
                className="btn btn-block"
                onClick={() => setScope('all')}
              >
                {t('home.scope_all')}
              </button>
            </Card>
          ) : (
            <Card title={t('home.assigned')}>
              {(data?.assigned ?? []).map((issue) => (
                <Row
                  key={issue.key}
                  onClick={() => navigate(`/issues/${issue.key}`)}
                  title={
                    <>
                      <Ltr>{issue.key}</Ltr> · {issue.summary}
                    </>
                  }
                  subtitle={
                    <>
                      {issue.issueTypeName}
                      {issue.priorityName ? ` · ${issue.priorityName}` : ''}
                      {issue.dueDate ? ` · ${issue.dueDate}` : ''}
                    </>
                  }
                  right={<StatusChip name={issue.statusName} category={issue.statusCategoryKey} />}
                />
              ))}
            </Card>
          )}
        </>
      ) : (
        <>
          <div className="grid">
            {siteTiles.map((tile) => (
              <button
                key={tile.labelKey}
                type="button"
                className="tile"
                onClick={() => navigate(`/issues?jql=${encodeURIComponent(tile.jql)}`)}
              >
                <div className="tile-value">{tile.value ?? '–'}</div>
                <div className="tile-label">{t(tile.labelKey)}</div>
              </button>
            ))}
          </div>

          <Card title={t('home.browse_projects')}>
            {(data?.projects ?? []).length === 0 ? (
              <Empty text={t('home.no_projects')} />
            ) : (
              (data?.projects ?? []).map((project) => (
                <Row
                  key={project.key}
                  onClick={() =>
                    navigate(
                      `/issues?jql=${encodeURIComponent(
                        `project = ${project.key} AND resolution = Unresolved ORDER BY updated DESC`,
                      )}`,
                    )
                  }
                  title={
                    <>
                      <Ltr>{project.key}</Ltr> · {project.name}
                    </>
                  }
                  subtitle={t('home.open_issues')}
                />
              ))
            )}
          </Card>
        </>
      )}

      <div className="btn-row">
        <button type="button" className="btn" onClick={() => navigate('/create')}>
          ＋ {t('create.pick_project')}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/access')}>
          🛡 {t('access.title').replace(/<[^>]+>/g, '')}
        </button>
      </div>
    </div>
  );
}
